import { useEffect, useState, useRef } from "react";
import api, { apiError, API, TOKEN_KEY, isAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Lock, Sparkles, AlertTriangle, CalendarClock, Clock, CheckCircle2, ListChecks, RefreshCw,
  FileText, Repeat, CreditCard, CalendarDays, Settings as Cog, Plus, Trash2, Upload, Eye,
  Wand2, Download, Loader2, Bell,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: Sparkles },
  { key: "tasks", label: "Tasks", icon: ListChecks },
  { key: "renewals", label: "Renewals", icon: Repeat, flag: "renewal_reminders_enabled" },
  { key: "subscriptions", label: "Subscriptions", icon: CreditCard, flag: "subscription_tracking_enabled" },
  { key: "documents", label: "Documents", icon: FileText, flag: "document_uploads_enabled" },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "settings", label: "Settings", icon: Cog },
];
const prioColor = { high: "bg-red-500/20 text-red-300", medium: "bg-amber-500/20 text-amber-300", low: "bg-white/10 text-muted-foreground" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "No date");

const Card = ({ children, className = "" }) => <div className={`card-emerald p-5 ${className}`}>{children}</div>;
const Empty = ({ children }) => <p className="text-sm text-muted-foreground py-8 text-center">{children}</p>;

export default function PersonalAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [cfg, setCfg] = useState(null);
  const [ov, setOv] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [subs, setSubs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [cal, setCal] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // {type, data}
  const [extract, setExtract] = useState(null); // {doc, suggestions, extracted}
  const fileRef = useRef();

  const loadCfg = async () => { try { setCfg((await api.get("/personal/config")).data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const loadOverview = async () => { try { setOv((await api.get("/personal/overview")).data); } catch {} };
  const loaders = {
    overview: loadOverview,
    tasks: async () => setTasks((await api.get("/personal/tasks")).data),
    renewals: async () => setRenewals((await api.get("/personal/renewals")).data),
    subscriptions: async () => setSubs((await api.get("/personal/subscriptions")).data),
    documents: async () => setDocs((await api.get("/personal/documents")).data),
    calendar: async () => setCal((await api.get("/personal/calendar")).data),
    settings: async () => {},
  };

  useEffect(() => { loadCfg(); loadOverview(); }, []);
  useEffect(() => { loaders[tab]?.().catch(() => {}); /* eslint-disable-line */ }, [tab]);

  const flags = cfg?.flags || {};
  const cats = cfg?.categories || [];
  const rtypes = cfg?.renewal_types || [];
  const visibleTabs = TABS.filter((t) => !t.flag || flags[t.flag]);

  const refreshSummary = async () => {
    setBusy(true);
    try { const { data } = await api.post("/personal/summary/refresh"); setOv((o) => ({ ...o, ai_summary: data.text, ai_summary_at: data.generated_at })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // ---- CRUD helpers ----
  const save = async () => {
    const { type, data } = dialog;
    if (data.cost === "") data.cost = null;
    try {
      if (type === "task") await api.post("/personal/tasks", data);
      if (type === "renewal") await api.post("/personal/renewals", data);
      if (type === "subscription") await api.post("/personal/subscriptions", data);
      toast.success("Saved");
      setDialog(null);
      loaders[type === "subscription" ? "subscriptions" : type + "s"]?.();
      loadOverview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const patchTask = async (t, body) => { await api.patch(`/personal/tasks/${t.id}`, body); loaders.tasks(); loadOverview(); };
  const del = async (kind, id) => { await api.delete(`/personal/${kind}/${id}`); loaders[kind === "tasks" ? "tasks" : kind]?.(); loadOverview(); toast.success("Removed"); };

  // ---- documents ----
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", "Documents");
    setBusy(true);
    try { await api.post("/personal/documents", fd); toast.success("Uploaded"); loaders.documents(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const viewDoc = (id) => window.open(`${API}/personal/documents/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}`, "_blank");
  const runExtract = async (doc) => {
    setBusy(true);
    try { const { data } = await api.post(`/personal/documents/${doc.id}/extract`); setExtract({ doc, ...data }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const createFromSuggestion = async (s) => {
    try {
      await api.post("/personal/renewals", { name: s.name, type: "Other", provider: s.provider, renewal_date: s.renewal_date, document_id: extract.doc.id });
      toast.success("Renewal reminder created"); setExtract(null); loadOverview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const OBItem = ({ it }) => (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={`ob-${it.kind}-${it.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{it.title}</p>
        <p className="text-xs text-muted-foreground capitalize">{it.kind} · {it.category || "—"}{it.when ? ` · ${it.when}` : ""}</p>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">{fmt(it.date)}</span>
    </div>
  );

  return (
    <div data-testid="personal-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold">Personal Admin</h1>
            <Badge className="bg-emerald-500/15 text-emerald-300 gap-1"><Lock className="w-3 h-3" /> Private</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Your own administrative workspace — {isAdmin(user) ? "kept completely separate from your management tools." : "only you can see this."}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 border-b border-emerald-500/10">
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ptab-${t.key}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === t.key ? "bg-emerald-500/15 text-emerald-200" : "text-muted-foreground hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ---------------- OVERVIEW ---------------- */}
      {tab === "overview" && ov && (
        <div className="space-y-6" data-testid="personal-overview">
          {flags.ai_summaries_enabled && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-400" /><span className="font-semibold">What needs your attention</span></div>
                <Button size="sm" variant="outline" onClick={refreshSummary} disabled={busy} data-testid="refresh-summary-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}<span className="ml-1">Refresh</span>
                </Button>
              </div>
              <p className="text-sm leading-relaxed text-emerald-50/90" data-testid="ai-summary-text">{ov.ai_summary || "Tap Refresh to generate your AI summary."}</p>
            </Card>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["attention", "Requires attention", AlertTriangle, "text-amber-400"], ["overdue", "Overdue", Clock, "text-red-400"], ["upcoming", "Upcoming", CalendarClock, "text-sky-400"], ["completed", "Completed", CheckCircle2, "text-emerald-400"]].map(([k, l, Ic, c]) => (
              <Card key={k} className="!p-4"><div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-muted-foreground">{l}</span><Ic className={`w-4 h-4 ${c}`} /></div><p className="font-display text-3xl font-extrabold mt-1">{ov.counts[k]}</p></Card>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card><h3 className="font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Requires attention</h3>{ov.requires_attention.length ? ov.requires_attention.map((it) => <OBItem key={it.id} it={it} />) : <Empty>Nothing this week.</Empty>}</Card>
            <Card><h3 className="font-semibold mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-red-400" /> Overdue</h3>{ov.overdue.length ? ov.overdue.map((it) => <OBItem key={it.id} it={it} />) : <Empty>Nothing overdue. 🎉</Empty>}</Card>
            <Card><h3 className="font-semibold mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-sky-400" /> Upcoming</h3>{ov.upcoming.length ? ov.upcoming.slice(0, 8).map((it) => <OBItem key={it.id} it={it} />) : <Empty>Nothing upcoming.</Empty>}</Card>
            <Card><h3 className="font-semibold mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Recently completed</h3>{ov.completed.length ? ov.completed.map((it) => <OBItem key={it.id} it={it} />) : <Empty>Nothing yet.</Empty>}</Card>
          </div>
        </div>
      )}

      {/* ---------------- TASKS ---------------- */}
      {tab === "tasks" && (
        <div data-testid="personal-tasks">
          <div className="flex justify-end mb-4"><Button className="bg-emerald-500 text-[#062217]" onClick={() => setDialog({ type: "task", data: { title: "", category: cats[0] || "Other", priority: "medium", status: "not_started", recurrence: "none", due_date: "" } })} data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" /> New task</Button></div>
          <div className="space-y-2">
            {tasks.length === 0 && <Card><Empty>No personal tasks yet.</Empty></Card>}
            {tasks.map((t) => (
              <Card key={t.id} className="!p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`font-medium truncate ${t.status === "completed" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.category} · due {fmt(t.due_date)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={prioColor[t.priority]}>{t.priority}</Badge>
                    <Select value={t.status} onValueChange={(v) => patchTask(t, { status: v })}>
                      <SelectTrigger className="w-36 bg-[#0A0D0C] border-emerald-500/20 h-8 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">{["not_started", "in_progress", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="text-red-400 h-8 w-8" onClick={() => del("tasks", t.id)} data-testid={`del-task-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- RENEWALS ---------------- */}
      {tab === "renewals" && (
        <div data-testid="personal-renewals">
          <div className="flex justify-end mb-4"><Button className="bg-emerald-500 text-[#062217]" onClick={() => setDialog({ type: "renewal", data: { name: "", type: rtypes[0] || "Other", provider: "", renewal_date: "", cost: null, currency: cfg?.settings?.currency || "USD", recurrence: "yearly" } })} data-testid="add-renewal-btn"><Plus className="w-4 h-4 mr-1" /> New renewal</Button></div>
          <div className="grid md:grid-cols-2 gap-3">
            {renewals.length === 0 && <Card className="md:col-span-2"><Empty>No renewals tracked yet.</Empty></Card>}
            {renewals.map((r) => (
              <Card key={r.id} className="!p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{r.name}</p>
                  <Button size="icon" variant="ghost" className="text-red-400 h-7 w-7" onClick={() => del("renewals", r.id)} data-testid={`del-renewal-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs text-muted-foreground">{r.type}{r.provider ? ` · ${r.provider}` : ""}</p>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="flex items-center gap-1 text-emerald-300"><CalendarClock className="w-3.5 h-3.5" /> {fmt(r.renewal_date)}</span>
                  {r.cost ? <span>{r.currency} {r.cost}</span> : null}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><Bell className="w-3 h-3" /> Reminders: {(r.reminder_offsets || []).join(", ")} days before</p>
                <Button size="sm" variant="outline" className="mt-2 w-full border-emerald-500/30" onClick={async () => { await api.post(`/personal/renewals/${r.id}/mark-paid`); toast.success("Marked paid — rescheduled to next date"); loaders.renewals(); loadOverview(); }} data-testid={`renewal-paid-${r.id}`}>Mark paid &amp; reschedule</Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- SUBSCRIPTIONS ---------------- */}
      {tab === "subscriptions" && (
        <div data-testid="personal-subscriptions">
          <div className="flex justify-end mb-4"><Button className="bg-emerald-500 text-[#062217]" onClick={() => setDialog({ type: "subscription", data: { name: "", service: "", cost: null, currency: cfg?.settings?.currency || "USD", billing_frequency: "monthly", renewal_date: "", category: "Subscription" } })} data-testid="add-sub-btn"><Plus className="w-4 h-4 mr-1" /> New subscription</Button></div>
          <div className="grid md:grid-cols-2 gap-3">
            {subs.length === 0 && <Card className="md:col-span-2"><Empty>No subscriptions tracked yet.</Empty></Card>}
            {subs.map((s) => (
              <Card key={s.id} className="!p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{s.name}</p>
                  <div className="flex items-center gap-1">
                    <Select value={s.status} onValueChange={async (v) => { await api.patch(`/personal/subscriptions/${s.id}`, { status: v }); loaders.subscriptions(); loadOverview(); }}>
                      <SelectTrigger className="w-28 h-7 text-xs bg-[#0A0D0C] border-emerald-500/20" data-testid={`sub-status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">{["active", "paused", "cancelled"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="text-red-400 h-7 w-7" onClick={() => del("subscriptions", s.id)} data-testid={`del-sub-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{s.service || s.category} · {s.billing_frequency}</p>
                <div className="flex items-center justify-between mt-2 text-sm"><span className="text-emerald-300">Renews {fmt(s.renewal_date)}</span>{s.cost ? <span>{s.currency} {s.cost}</span> : null}</div>
                <Button size="sm" variant="outline" className="mt-2 w-full border-emerald-500/30" onClick={async () => { await api.post(`/personal/subscriptions/${s.id}/mark-paid`); toast.success("Marked paid — rescheduled to next date"); loaders.subscriptions(); loadOverview(); }} data-testid={`sub-paid-${s.id}`}>Mark paid &amp; reschedule</Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- DOCUMENTS ---------------- */}
      {tab === "documents" && (
        <div data-testid="personal-documents">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground">Private & encrypted. Upload a document, then let AI read key dates.</p>
            <input ref={fileRef} type="file" className="hidden" onChange={onUpload} accept="image/*,.pdf" data-testid="doc-file-input" />
            <Button className="bg-emerald-500 text-[#062217]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-doc-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {docs.length === 0 && <Card className="md:col-span-2"><Empty>No documents uploaded yet.</Empty></Card>}
            {docs.map((dc) => (
              <Card key={dc.id} className="!p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-emerald-400 shrink-0" /><p className="font-medium truncate">{dc.original_filename}</p></div>
                  <Badge className="bg-white/10 text-muted-foreground shrink-0">{dc.category}</Badge>
                </div>
                {dc.extracted && <p className="text-[11px] text-emerald-300/80 mt-2">AI read: {dc.extracted.document_type || "document"}{dc.extracted.expiration_date ? ` · expires ${dc.extracted.expiration_date}` : ""}</p>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => viewDoc(dc.id)} data-testid={`view-doc-${dc.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
                  {dc.extractable && flags.document_extraction_enabled && <Button size="sm" variant="outline" onClick={() => runExtract(dc)} disabled={busy} data-testid={`extract-doc-${dc.id}`}><Wand2 className="w-3.5 h-3.5 mr-1" /> Read dates</Button>}
                  <Button size="sm" variant="ghost" className="text-red-400 ml-auto" onClick={() => del("documents", dc.id)} data-testid={`del-doc-${dc.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- CALENDAR ---------------- */}
      {tab === "calendar" && (
        <div data-testid="personal-calendar" className="space-y-5">
          {["overdue", "today", "this_week", "upcoming"].map((b) => {
            const items = cal.filter((i) => i.bucket === b);
            const labels = { overdue: ["Overdue", "text-red-400"], today: ["Due today", "text-amber-400"], this_week: ["This week", "text-sky-400"], upcoming: ["Upcoming", "text-emerald-400"] };
            return (
              <Card key={b}>
                <h3 className={`font-semibold mb-2 ${labels[b][1]}`}>{labels[b][0]}</h3>
                {items.length ? items.map((it) => <OBItem key={`${it.kind}-${it.id}`} it={it} />) : <Empty>Nothing here.</Empty>}
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------------- SETTINGS ---------------- */}
      {tab === "settings" && cfg && <PersonalSettings cfg={cfg} onSaved={loadCfg} />}

      {/* ---------------- CREATE DIALOG ---------------- */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20" data-testid="personal-create-dialog">
          <DialogHeader><DialogTitle className="capitalize">New {dialog?.type}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              {dialog.type === "task" && <>
                <FieldRow label="Title"><Input autoFocus className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.title = e.target.value)} data-testid="task-title-input" /></FieldRow>
                <FieldRow label="Description"><Textarea className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.description = e.target.value)} /></FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <PickRow label="Category" value={dialog.data.category} onChange={(v) => (dialog.data.category = v)} options={cats} />
                  <PickRow label="Priority" value={dialog.data.priority} onChange={(v) => (dialog.data.priority = v)} options={["low", "medium", "high"]} />
                </div>
                <FieldRow label="Due date"><Input type="date" className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.due_date = e.target.value)} data-testid="task-due-input" /></FieldRow>
              </>}
              {dialog.type === "renewal" && <>
                <FieldRow label="Name"><Input autoFocus className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.name = e.target.value)} data-testid="renewal-name-input" /></FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <PickRow label="Type" value={dialog.data.type} onChange={(v) => (dialog.data.type = v)} options={rtypes} />
                  <FieldRow label="Provider"><Input className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.provider = e.target.value)} /></FieldRow>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Renewal date"><Input type="date" className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.renewal_date = e.target.value)} data-testid="renewal-date-input" /></FieldRow>
                  <FieldRow label="Cost"><Input type="number" className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.cost = Number(e.target.value) || null)} /></FieldRow>
                </div>
              </>}
              {dialog.type === "subscription" && <>
                <FieldRow label="Name"><Input autoFocus className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.name = e.target.value)} data-testid="sub-name-input" /></FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Service / provider"><Input className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.service = e.target.value)} /></FieldRow>
                  <PickRow label="Billing" value={dialog.data.billing_frequency} onChange={(v) => (dialog.data.billing_frequency = v)} options={["weekly", "monthly", "quarterly", "yearly"]} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Renewal date"><Input type="date" className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.renewal_date = e.target.value)} data-testid="sub-date-input" /></FieldRow>
                  <FieldRow label="Cost"><Input type="number" className="bg-[#0A0D0C] border-emerald-500/20" onChange={(e) => (dialog.data.cost = Number(e.target.value) || null)} /></FieldRow>
                </div>
              </>}
            </div>
          )}
          <DialogFooter><Button className="bg-emerald-500 text-[#062217]" onClick={save} data-testid="save-personal-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- EXTRACTION SUGGESTION ---------------- */}
      <Dialog open={!!extract} onOpenChange={(o) => !o && setExtract(null)}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20" data-testid="extract-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-emerald-400" /> AI read your document</DialogTitle>
            <DialogDescription>Nothing is created automatically — confirm what you want to keep.</DialogDescription></DialogHeader>
          {extract && (
            <div className="space-y-3 text-sm">
              <div className="bg-white/5 rounded-lg p-3 space-y-1">
                {Object.entries(extract.extracted || {}).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3"><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span><span className="text-right">{Array.isArray(v) ? v.map((x) => x.date || JSON.stringify(x)).join(", ") : String(v)}</span></div>
                ))}
                {Object.keys(extract.extracted || {}).length === 0 && <p className="text-muted-foreground">No dates found. You can add a renewal manually.</p>}
              </div>
              {extract.suggestions?.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-2 border border-emerald-500/20 rounded-lg p-3">
                  <div><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground">Renewal reminder for {fmt(s.renewal_date)}</p></div>
                  <Button size="sm" className="bg-emerald-500 text-[#062217]" onClick={() => createFromSuggestion(s)} data-testid="create-reminder-btn">Create reminder</Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setExtract(null)} data-testid="ignore-extract-btn">Ignore</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const FieldRow = ({ label, children }) => <div><label className="text-xs text-muted-foreground">{label}</label><div className="mt-1">{children}</div></div>;
const PickRow = ({ label, value, onChange, options }) => {
  const [v, setV] = useState(value);
  return <div><label className="text-xs text-muted-foreground">{label}</label>
    <Select defaultValue={value} onValueChange={(x) => { setV(x); onChange(x); }}>
      <SelectTrigger className="bg-[#0A0D0C] border-emerald-500/20 mt-1 capitalize"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#121816] border-emerald-500/20 max-h-60">{options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
    </Select></div>;
};

function PersonalSettings({ cfg, onSaved }) {
  const s = cfg.settings || {};
  const [offsets, setOffsets] = useState((s.reminder_offsets || cfg.reminder_defaults || []).join(", "));
  const [email, setEmail] = useState(s.email_reminders ?? true);
  const [inapp, setInapp] = useState(s.inapp_reminders ?? true);
  const [currency, setCurrency] = useState(s.currency || "USD");
  const [confirmDel, setConfirmDel] = useState(false);

  const saveSettings = async () => {
    const arr = offsets.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n) && n >= 0);
    try { await api.put("/personal/settings", { reminder_offsets: arr, email_reminders: email, inapp_reminders: inapp, currency }); toast.success("Settings saved"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const exportData = async () => {
    try {
      const { data } = await api.get("/personal/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = "qwiklogs-personal-admin.json"; a.click(); URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteAll = async () => { try { await api.delete("/personal/data", { data: { confirm: true } }); toast.success("All personal data deleted"); setConfirmDel(false); onSaved(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="personal-settings">
      <Card>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Bell className="w-4 h-4 text-emerald-400" /> Reminder preferences</h3>
        <label className="text-xs text-muted-foreground">Remind me these many days before (comma separated)</label>
        <Input value={offsets} onChange={(e) => setOffsets(e.target.value)} className="bg-[#0A0D0C] border-emerald-500/20 mt-1" data-testid="reminder-offsets-input" />
        <div className="flex items-center justify-between mt-4"><span className="text-sm">Email reminders</span><Switch checked={email} onCheckedChange={setEmail} data-testid="email-reminders-switch" /></div>
        <div className="flex items-center justify-between mt-2"><span className="text-sm">In-app reminders</span><Switch checked={inapp} onCheckedChange={setInapp} data-testid="inapp-reminders-switch" /></div>
        <Button className="bg-emerald-500 text-[#062217] mt-4" onClick={saveSettings} data-testid="save-settings-btn">Save preferences</Button>
      </Card>
      <Card>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-400" /> Currency</h3>
        <label className="text-xs text-muted-foreground">Preferred currency for your costs &amp; subscriptions</label>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="bg-[#0A0D0C] border-emerald-500/20 mt-1" data-testid="currency-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#121816] border-emerald-500/20 max-h-60">{(cfg.currencies || ["USD"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="bg-emerald-500 text-[#062217] mt-4" onClick={saveSettings} data-testid="save-currency-btn">Save currency</Button>
      </Card>
      <Card>
        <h3 className="font-semibold mb-2">Your data</h3>
        <p className="text-xs text-muted-foreground mb-3">Export or permanently delete all your Personal Admin data. This never touches company/work data.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportData} data-testid="export-data-btn"><Download className="w-4 h-4 mr-1" /> Export my data</Button>
          <Button variant="outline" className="border-red-500/40 text-red-300" onClick={() => setConfirmDel(true)} data-testid="delete-data-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete all</Button>
        </div>
      </Card>
      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20">
          <DialogHeader><DialogTitle>Delete all Personal Admin data?</DialogTitle><DialogDescription>This removes all your personal tasks, renewals, subscriptions and documents. It cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDel(false)}>Cancel</Button><Button className="bg-red-500 text-white" onClick={deleteAll} data-testid="confirm-delete-data-btn">Delete everything</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
